import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { GoogleMap, LoadScript, Marker } from "@react-google-maps/api";

interface MapDialogProps {
  open: boolean;
  onClose: () => void;
  venueName: string;
  address: string | null;
  primaryColor: string;
  googleMapsApiKey: string;
}

const mapContainerStyle = {
  width: "100%",
  height: "400px",
};

export const MapDialog: React.FC<MapDialogProps> = ({
  open,
  onClose,
  venueName,
  address,
  primaryColor,
  googleMapsApiKey,
}) => {
  const [location, setLocation] = useState<{ lat: number; lng: number } | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(false);

  const fallbackCenter = useMemo(
    () => ({
      lat: -17.8252,
      lng: 31.0335,
    }),
    [],
  );

  useEffect(() => {
    const fetchCoordinates = async () => {
      if (!address) {
        setLocation(fallbackCenter);
        return;
      }

      setIsLoading(true);
      try {
        const response = await fetch(
          `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
            address,
          )}&key=${googleMapsApiKey}`,
        );
        const data = await response.json();
        if (data.results?.[0]) {
          const { lat, lng } = data.results[0].geometry.location;
          setLocation({ lat, lng });
        } else {
          setLocation(fallbackCenter);
        }
      } catch (error) {
        console.error("Geocoding error:", error);
        setLocation(fallbackCenter);
      } finally {
        setIsLoading(false);
      }
    };

    if (open) {
      fetchCoordinates();
    } else {
      setIsLoading(false);
      setLocation(null);
    }
  }, [fallbackCenter, open, address, googleMapsApiKey]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: { borderRadius: 3 },
      }}
    >
      {isLoading && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            zIndex: 1,
          }}
        >
          <CircularProgress />
        </Box>
      )}
      <DialogTitle
        sx={{
          color: primaryColor,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        Location - {venueName}
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <LoadScript googleMapsApiKey={googleMapsApiKey}>
          {location && (
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              zoom={15}
              center={location}
            >
              <Marker position={location} />
            </GoogleMap>
          )}
        </LoadScript>
      </DialogContent>
    </Dialog>
  );
};

export default MapDialog;
